/**
 * @fileoverview Mock-only user lifecycle change feed.
 * @description In mock mode User Management (features/user-invitations' mock store) and the Sales
 * roster (features/sales' mock store) are separate in-memory data sets, and features may not
 * import each other (ADR-0002). A real backend has one user table: deactivating a user there is
 * seen by the roster and the owner pickers at once. This feed gives the mocks the same effect. The
 * user-management mock records each deactivation and reactivation it applies; the sales mock
 * subscribes and applies the same change to its roster and targets, so the refetch a lifecycle
 * command triggers reads the new state. Subscribers also receive the changes recorded before they
 * subscribed, so it does not matter which mock module loads first. Never imported outside mocks.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

/** A user was deactivated; their direct reports moved to the recipient (their own manager). */
export interface UserDeactivatedChange {
  kind: 'DEACTIVATED'
  userId: string
  /** Who took over the user's direct reports, or null when the user had no manager. */
  recipientUserId: string | null
  /** The direct reports that moved to the recipient. */
  movedReportUserIds: string[]
  /** Who ran the command, recorded on each transferred target. */
  actingUserId: string
}

/** A user was reactivated with the role and reporting manager they came back with. */
export interface UserReactivatedChange {
  kind: 'REACTIVATED'
  userId: string
  /** The role's display name, as the Sales roster names roles. */
  roleName: string
  reportingManagerUserId: string
  /** True when the user never activated and is invited again, rather than Active at once. */
  invitedAgain: boolean
}

export type UserLifecycleChange = UserDeactivatedChange | UserReactivatedChange

export type UserLifecycleListener = (change: UserLifecycleChange) => void

const history: UserLifecycleChange[] = []
const listeners = new Set<UserLifecycleListener>()

/**
 * @description Records a lifecycle change and tells every subscriber.
 * @param {UserLifecycleChange} change - What the deactivation or reactivation changed.
 */
export function recordUserLifecycleChange(change: UserLifecycleChange): void {
  history.push(change)
  for (const listener of listeners) {
    listener(change)
  }
}

/**
 * @description Subscribes to lifecycle changes, replaying the ones already recorded first.
 * @param {UserLifecycleListener} listener - Called with each change, past and future.
 * @returns {() => void} Stops the subscription.
 */
export function subscribeToUserLifecycleChanges(listener: UserLifecycleListener): () => void {
  for (const change of history) {
    listener(change)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * @description Forgets every recorded change and subscriber, for tests that need a clean feed.
 */
export function resetUserLifecycleChanges(): void {
  history.length = 0
  listeners.clear()
}
